import React from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import { ROUTES } from "../../../routes/paths";
import {
  Button,
  EmptyStateProps,
  failedEmptyState,
  useIsMobile,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
// The same mark the clients screen shows over its own first-run state, so a
// workspace with no clients meets one invitation rather than two.
import { ReactComponent as FirstClientMark } from "../../../assets/svg/No data images/my client.svg";
import WelcomeCard from "../components/WelcomeCard";
import StatTile from "../components/StatTile";
import EarningsCard from "../components/EarningsCard";
import NewClientsCard from "../components/NewClientsCard";
import TotalClientsCard from "../components/TotalClientsCard";
import RecentClientsTable from "../components/RecentClientsTable";
import MobileDashboard from "../components/MobileDashboard";
import BrandWelcomeModal from "../components/BrandWelcomeModal";
import SwitchToDesktopModal from "../components/SwitchToDesktopModal";
import UpgradeBanner from "../components/UpgradeBanner";
import { termCountdown } from "../../billing/termCountdown";
import { useDashboard } from "../hooks/useDashboard";
import { useBrandSetUp } from "../hooks/useBrandSetUp";
// Plans are the brand feature's, and so is the read that says whether the
// workspace sells any — the checklist's second tick and the gate over Add
// client both ask it, so it lives where a plan is made rather than here.
import { usePlansCreated } from "../../brand/hooks/usePlansCreated";
import { useBrandWelcome } from "../hooks/useBrandWelcome";
import { useDesktopNotice } from "../hooks/useDesktopNotice";
import { useAccessLapse } from "../../billing/hooks/useAccessLapse";
import { useCurrentSubscription } from "../../billing/hooks/useCurrentSubscription";
import { gateReason } from "../../billing/hooks/useSubscriptionGate";
import { hasAccess } from "../../billing/subscriptionState";
import { isSetupComplete, setupSteps } from "../setupSteps";

/** What a tile shows in place of a count nobody has measured yet. */
const NO_FIGURE = "-";

const DashboardPage = () => {
  const navigate = useNavigate();
  const toast = useToast();

  /**
   * Which of the two screens this is.
   *
   * Everything below is common to both — the same four reads, the same phase,
   * the same figures and the same rules about what a locked workspace may do.
   * All that differs is how they are laid out, and on a phone that is not a
   * matter of narrower columns: see `MobileDashboard`.
   *
   * Asked here rather than inside each card, so the answer is one render
   * rather than a dozen, and so the page is never half of one screen and half
   * of the other.
   */
  const isMobile = useIsMobile();

  const { summary, status, reload } = useDashboard();
  const { brandSetUp, status: brandStatus } = useBrandSetUp();

  // Whether anything is sold yet, for the checklist's middle step.
  const { plansCreated, status: plansStatus } = usePlansCreated();

  // What the workspace is actually on. Not a step of the checklist any more —
  // the upgrade banner below is what asks for a plan — but still what decides
  // that banner and what this screen's create buttons are allowed to do. The
  // trial comes with it because it is the other way the product is open.
  const {
    accessSource,
    termDaysLeft,
    status: subscriptionStatus,
  } = useCurrentSubscription();

  // What ran out, for the workspace whose door is shut: the fact the strip and
  // the greyed Add client button are both worded from. It takes a second read —
  // the subscription call cannot tell a lapsed plan from a spent trial on its
  // own — and it is the same answer `useSubscriptionGate` gives the other
  // screens, so the two cannot drift. See `accessLapse`.
  const { lapse } = useAccessLapse();

  // Only once the brand read has actually answered: a call that failed is not
  // a brand that is missing, and the greeting is not worth putting to someone
  // who set theirs up months ago. Same caution as the checklist below.
  const brandWelcome = useBrandWelcome(brandStatus === "ready" && !brandSetUp);

  // The note that this app is at its best on a wide screen: made to a practice
  // that has just signed up, on a phone, and to nobody else. Nothing it is told
  // about has to have loaded first — it is about the device and how this visit
  // began, rather than about the workspace — so it does not wait on the four
  // reads below and is up as the screen arrives.
  const desktopNotice = useDesktopNotice(isMobile);

  /**
   * Whether the screen is still waiting on anything at all.
   *
   * All four reads, not just the dashboard's own: the welcome card is decided
   * by the brand and the plans, the banner by the subscription, and a card
   * that lands after the rest reads as the screen arriving twice. So they are
   * one answer as far as this screen is concerned — it waits for the slowest.
   */
  const loading =
    status === "loading" ||
    brandStatus === "loading" ||
    plansStatus === "loading" ||
    subscriptionStatus === "loading";

  // One phase drives every card on the screen — they wait together, shimmer
  // together, and land together. A dashboard that answers quickly never
  // reaches the shimmer at all.
  const phase = useLoadPhase(loading);

  // A workspace with no clients has no earnings and nothing converted either —
  // a client is what a paid request turns into — so one measured zero puts
  // every card on the screen into the state it opens a new workspace in.
  const noClientsYet = summary.totalClients === 0;

  // Whether there is a plan behind this workspace, which is what the upgrade
  // banner is about and nothing more — a workspace on its free trial has no
  // plan yet either, and the banner is the offer it is spending the fortnight
  // deciding on. What may be *done* here is `createLocked` below.
  //
  // Off `access_source`, the server's word for what is holding the door: a
  // plan is one only when the server says `SUBSCRIPTION`, whatever the record
  // beside it reads. False until the read has said so, which is what keeps the
  // banner off a screen that does not yet know whether there is anything to
  // upgrade from.
  const notSubscribed =
    subscriptionStatus === "ready" && accessSource !== "SUBSCRIPTION";

  /**
   * The last week of a term that is still running, or `null` on every other
   * day of it — see `termCountdown`.
   *
   * The one state the strip is drawn in for a workspace that *is* subscribed,
   * which is why it is a second fact rather than another branch of the one
   * above. Everything else the strip says is about a door that is shut; this
   * is said while it is still open, and it is the only warning a practice gets
   * before a term lapses underneath them. Nothing renews itself here — see
   * `prepaid.types` — so a term that runs out with nobody told simply takes
   * the product away on a Tuesday morning.
   *
   * Only while the server still calls the door open. The morning after the
   * last day `access_source` goes to `NONE`, and what is owed then is
   * `lapse`'s "Subscription ended", not a countdown that has run out.
   */
  const ending = notSubscribed ? null : termCountdown(termDaysLeft);

  /**
   * What everything on this screen that adds a client asks by — which is not
   * quite the fact above.
   *
   * A running trial opens the product as fully as a plan does, and the server
   * says which is holding the door in the same field: `SUBSCRIPTION`,
   * `FREE_TRIAL`, or `NONE` for the day the trial runs out with nothing bought
   * behind it, which is what shuts every entry point — here, on the three list
   * screens, and on the create-plan screen — leaving the workspace readable and
   * nothing more.
   *
   * This is the same line `useSubscriptionGate` draws for the other three
   * screens — kept in step with it by hand because this screen already holds
   * the billing answer and must not ask for it twice.
   */
  const createLocked =
    subscriptionStatus === "ready" && !hasAccess(accessSource);

  /** Whether the fortnight is what has just run out, which is the one case the
   *  locked button words differently — a lapsed plan gets `NEEDS_PLAN`, which
   *  is what a practice between terms needs to read. See `gateReason`. */
  const trialEnded = lapse === "trial";

  /**
   * What the locked button says, which is not the same on both sides of the
   * fortnight: somebody whose trial ran out yesterday is pressing Add client
   * out of habit and is owed the reason they actually met, not a general note
   * about subscriptions. `gateReason` is the list screens' wording too, so the
   * dashboard does not invent a fourth way of saying the same thing.
   *
   * Worked out whether or not the button is locked — it is only read when it
   * is, and the alternative is a conditional around a string.
   */
  const lockedReason = gateReason("add a client", trialEnded);

  /** The checklist, or `null` where there is nothing left to tick and where any
   *  of the three reads it is worked out from failed — a step this screen
   *  cannot vouch for is not one to put in front of somebody. See
   *  `WelcomeCard`'s `setup`. */
  const setup = (() => {
    if (
      brandStatus !== "ready" ||
      plansStatus !== "ready" ||
      status !== "ready"
    ) {
      return null;
    }

    const steps = setupSteps({
      brandSetUp,
      plansCreated,
      hasClients: !noClientsYet,
      // The row is a link into the Add client screen, so it is shut by the same
      // fact as the button below it — otherwise the checklist would be the one
      // way in that a lapsed workspace still had.
      createLocked,
    });
    return isSetupComplete(steps) ? null : steps;
  })();

  // Both come straight from `GET /api/v1/dashboard/`. A null count is one the
  // backend has no figure for, and a "0" there would read as a measurement
  // rather than as a figure that does not exist yet.
  //
  // The tile counts `active_clients` rather than everyone on the books: it says
  // "Active clients", and the roster's whole size is what the pie card below
  // reports against the requests behind it.
  //
  // `emptyMessage` is only for the workspace that has not started — it is the
  // measured zero being put in words, and it is deliberately not what a null
  // gets: a figure nobody has counted is still the dash.
  const stats = [
    {
      id: "active-clients",
      label: "Active clients",
      value: summary.activeClients ?? NO_FIGURE,
      emptyMessage: noClientsYet ? "No active clients" : null,
    },
    {
      id: "plans-created",
      label: "Plans created",
      value: summary.plansCreated ?? NO_FIGURE,
      emptyMessage: noClientsYet ? "No plans created" : null,
    },
  ];

  /** What the recent clients table shows in place of rows. Nothing about
   *  loading: while the figures are out the table shimmers its rows, and the
   *  status line below is what says so in words.
   *
   *  There is no search on this table, so there are only the two cases: the
   *  dashboard call failed, or this workspace has no clients in it yet. */
  const recentEmptyState = (): EmptyStateProps =>
    status === "failed"
      ? failedEmptyState(
          "We could not load your dashboard",
          <Button size="sm" variant="secondary" onClick={reload}>
            Try again
          </Button>
        )
      : {
          // The same welcome the clients screen gives, and the same mark, so
          // the two read as one invitation rather than two different answers
          // to the same emptiness. No highlights here: this card is one band
          // of a full dashboard, and the list belongs on the screen the
          // button leads to. Without them the block centres — see `EmptyState`.
          variant: "welcome",
          illustration: <FirstClientMark aria-hidden className="w-11 h-11" />,
          title: "Add your first client",
          description:
            "Start building your client list to manage consultations, meal plans, and progress.",
          action: (
            <Button
              size="md"
              startIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate(ROUTES.addClient)}
              disabled={createLocked}
              title={createLocked ? lockedReason.heading : undefined}
              onDisabledClick={() =>
                toast.warning(lockedReason.heading, lockedReason.detail)
              }
            >
              Add client
            </Button>
          ),
        };

  return (
    <>
      {/* A phone that has just signed up is due both of these at once, and two
          panels stacked on the same edge is neither of them being read. The
          desktop note goes first — it is about the screen being held rather
          than about the workspace, and it is answered by one press — and the
          brand dialog follows it the moment it closes. */}
      {desktopNotice.open && (
        <SwitchToDesktopModal onDismiss={desktopNotice.dismiss} />
      )}

      {/* Over the top of the screen rather than after it: this is the first
          thing a workspace with no brand is asked, and the dashboard behind it
          has nothing on it yet to interrupt. Outside the two layouts below
          because it stands over either of them — it is a dialog about the
          workspace rather than a band of the page. */}
      {brandWelcome.open && !desktopNotice.open && (
        <BrandWelcomeModal
          onSetupBrand={() => {
            brandWelcome.dismiss();
            navigate(ROUTES.myBrand);
          }}
          onDismiss={brandWelcome.dismiss}
        />
      )}

      {/* Said as soon as the request goes out, whether or not anything is
          drawn for it: this line is not seen, so it costs nothing to be early,
          and the blocks it stands for are hidden from screen readers — they
          are the shape of an answer rather than the answer. Once for both
          layouts, since both are waiting on the same four reads. */}
      {loading && (
        <p role="status" className="sr-only">
          Loading your dashboard…
        </p>
      )}

      {isMobile ? (
        <MobileDashboard
          summary={summary}
          stats={stats}
          setup={setup}
          noClientsYet={noClientsYet}
          notSubscribed={notSubscribed}
          lapse={lapse}
          ending={ending}
          recentEmpty={recentEmptyState()}
          phase={phase}
        />
      ) : (
        <div className="p-page pt-page-top">
          {/* Only ever drawn from `md` up, since that is the only width this
              tree renders at now. A phone gets no bar of its own: the shell
              already names the app across the top there, and a second one
              saying "Dashboard" over the greeting that follows it would spend a
              fifth of the screen agreeing with the lit tab at the foot. What
              heads that screen instead is the greeting — see
              `MobileDashboard`. */}
          <PageHeader title="Dashboard" />

          {/* One rhythm down the screen rather than a margin per block: the title
              above is not drawn on a phone and the banner below is not drawn for a
              subscriber, so whichever block happens to be first must not carry a
              top margin of its own. `space-y` never gives one to a first child. */}
          <div className="space-y-4 sm:mt-5 sm:space-y-5">
            {/* Above the greeting, which is where the design puts it: it is the one
                thing on this screen that is not a figure about the practice, and a
                workspace with no plan meets it before anything it cannot use yet.
                Past the trial it reports the end of it rather than offering to keep
                something already gone — the same split the create buttons make. */}
            {(notSubscribed || ending) && (
              <UpgradeBanner ended={lapse} ending={ending} />
            )}

            {/* Whenever all three cards can't sit on one line, the welcome
                card takes a full row and the two stat tiles pair up beneath it.
                From xl everything fits across as 3 + 1 + 1 of 5 columns. */}
            <div className="grid gap-3 sm:gap-4 3xl:gap-5 grid-cols-2 xl:grid-cols-5">
              <div className="col-span-2 xl:col-span-3">
                <WelcomeCard
                  highlights={{
                    requests: summary.newRequestsInLast7Days,
                    plans: summary.plansCreatedInLast7Days,
                    renewals: summary.renewalsInNext7Days,
                  }}
                  setup={setup}
                  phase={phase}
                />
              </div>
              {stats.map((stat) => (
                <StatTile
                  key={stat.id}
                  label={stat.label}
                  value={stat.value}
                  emptyMessage={stat.emptyMessage}
                  phase={phase}
                />
              ))}
            </div>

            {/* The two chart cards get ~1.5x the width of the pie card, matching the
                design — an even 3-column split leaves the charts cramped. Three
                across only from xl; below that the pie card wraps to its own row. */}
            <div className="grid gap-4 3xl:gap-5 md:grid-cols-2 xl:grid-cols-[1.5fr_1.5fr_1fr]">
              <EarningsCard
                total={summary.totalIncome}
                data={summary.incomeByMonth}
                empty={noClientsYet}
                phase={phase}
              />
              <NewClientsCard
                data={summary.newClientsByMonth}
                empty={noClientsYet}
                phase={phase}
              />
              <TotalClientsCard
                paidClients={summary.activeClients}
                totalRequests={summary.totalRequests}
                conversionRate={summary.conversionRate}
                empty={noClientsYet}
                phase={phase}
              />
            </div>

            <RecentClientsTable
              clients={summary.recentClients}
              phase={phase}
              empty={recentEmptyState()}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardPage;
